import type { HttpAdapter } from './HttpAdapter';
import type {
  LoginRequestDto,
  LoginResponseDto,
  RefreshTokenRequestDto,
  TokenDto,
  ServiceRequestDto,
  ServiceResponseDto,
  ServiceType,
} from '../../types/api';

// Use a local alias so shared/web builds don't require Node global typings.
const global: any = globalThis as any;

/**
 * API Service - Main service for backend communication
 */
export class ApiService {
  private baseUrl: string;
  private httpAdapter: HttpAdapter;

  constructor(baseUrl: string, httpAdapter: HttpAdapter) {
    this.baseUrl = baseUrl;
    this.httpAdapter = httpAdapter;
  }

  /**
   * Get backend URL for debugging
   */
  getBackendUrl(): string {
    return this.baseUrl;
  }

  /**
   * Test connectivity to backend server
   */
  async testConnectivity(): Promise<boolean> {
    console.log('🔍 Testing backend connectivity...');
    console.log(`   Backend URL: ${this.baseUrl}`);
    
    try {
      // Try to make a simple GET request to the API base
      const response = await this.httpAdapter.get('/api');
      console.log('✅ Backend is reachable');
      console.log(`   Status: ${response.status}`);
      return true;
    } catch (error) {
      console.log('❌ Backend connectivity test failed');
      console.log(`   Error:`, (error as any)?.message || error);
      
      // Try the login endpoint as a fallback test
      try {
        console.log('🔄 Trying login endpoint as connectivity test...');
        await this.httpAdapter.post(this.authEndpoints.LOGIN, {});
      } catch (loginError) {
        console.log('❌ Login endpoint also unreachable');
        console.log(`   Error:`, (loginError as any)?.message || loginError);
        if ((loginError as any)?.code === 'ECONNREFUSED') {
          console.log('💡 Suggestion: Check if backend server is running');
        }
        if ((loginError as any)?.code === 'ENOTFOUND') {
          console.log('💡 Suggestion: Check network connectivity and URL');
        }
      }
      return false;
    }
  }

  // Authentication endpoints (relative paths - base URL handled by HTTP adapter)
  private get authEndpoints() {
    return {
      LOGIN: '/api/Authentication/login',
      REFRESH_TOKEN: '/api/Authentication/refreshtoken',
      LOGOUT: '/api/Authentication/logout',
      SEND_CODE: '/api/Authentication/sendcode',
      PASSKEY_CHALLENGE: '/api/Authentication/passkey/challenge',
      PASSKEY_VERIFY: '/api/Authentication/passkey/verify',
    };
  }

  // Service endpoints (relative paths - base URL handled by HTTP adapter)
  private get serviceEndpoints() {
    return {
      ACCOUNT: '/api/services/account',
      SYNC: '/api/services/sync',
      CONTENT: '/api/services/content',
      DATA: '/api/services/data',
      NOTIFY: '/api/services/notify',
      LOG: '/api/services/log',
      MKT: '/api/services/mkt',
      REPORT: '/api/services/report',
    };
  }

  /**
   * Authenticate user with backend
   */
  async authenticate(request: LoginRequestDto): Promise<LoginResponseDto> {
    const url = this.authEndpoints.LOGIN;
    
    try {
      const response = await this.httpAdapter.post<LoginResponseDto>(
        url,
        request,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error) {
      console.log('❌ HTTP request failed');
      console.log(`   Error:`, error);
      if ((error as any)?.response) {
        console.log(`   HTTP Status: ${(error as any).response.status}`);
        console.log(`   Response Data:`, (error as any).response.data);
      }
      if ((error as any)?.code) {
        console.log(`   Error Code: ${(error as any).code}`);
      }
      throw error;
    }
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(request: RefreshTokenRequestDto): Promise<{ success: boolean; data?: any; message?: string }> {
    const url = this.authEndpoints.REFRESH_TOKEN;
    
    try {
      const response = await this.httpAdapter.post<any>(
        url,
        request,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      // Check if the response has the new RefreshTokenResponseDto structure
      if (response.data?.success && response.data?.data) {
        const responseData = response.data.data;
        
        return {
          success: true,
          data: responseData  // Return the full data structure including profiles and adminHistory
        };
      } 
      else {
        return {
          success: false,
          message: (response.data as any)?.message || 'Invalid response structure - no token data found'
        };
      }
    } catch (error) {
      console.log('❌ Token refresh HTTP request failed');
      console.log(`   Error:`, error);
      if ((error as any)?.response) {
        console.log(`   HTTP Status: ${(error as any).response.status}`);
        console.log(`   Response Data:`, (error as any).response.data);
      }
      if ((error as any)?.code) {
        console.log(`   Error Code: ${(error as any).code}`);
      }
      
      return {
        success: false,
        message: (error as any)?.response?.data?.message || (error as any)?.message || 'Token refresh failed'
      };
    }
  }

  /**
   * Process service request
   */
  async processServiceRequest(
    request: ServiceRequestDto,
    serviceType: ServiceType = 'sync'
  ): Promise<ServiceResponseDto> {
    const endpointMap = {
      account: this.serviceEndpoints.ACCOUNT,
      sync: this.serviceEndpoints.SYNC,
      content: this.serviceEndpoints.CONTENT,
      data: this.serviceEndpoints.DATA,
      notify: this.serviceEndpoints.NOTIFY,
      log: this.serviceEndpoints.LOG,
      mkt: this.serviceEndpoints.MKT,
      report: this.serviceEndpoints.REPORT,
    };

    const endpoint = endpointMap[serviceType];
    console.log(`🔧 processServiceRequest: calling httpAdapter.post`, { endpoint, serviceType, requestType: request.RequestType });

    let response;
    try {
      response = await this.httpAdapter.post<ServiceResponseDto>(
        endpoint,
        request,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
    } catch (err: any) {
      console.error(`❌ processServiceRequest: httpAdapter.post threw`, { endpoint, message: err?.message, status: err?.response?.status, err });
      throw err;
    }

    return response.data;
  }

  /**
   * Process refresh token response and update tokens/session
   */
  private async processRefreshResponse(
    refreshResponse: any,
    userStore: any,
    userStoreState: any,
    storage?: any
  ): Promise<boolean> {
    if (refreshResponse?.success && refreshResponse.data?.token) {
      const newToken = refreshResponse.data.token;
      const newRefreshToken = refreshResponse.data.refreshToken;
      const profileData = refreshResponse.data.profiles;
      // Handle both camelCase (adminHistory) and all-lowercase (adminhistory) — the C# serializer
      // can return either depending on the controller's serializer settings.  Normalise here so
      // every downstream consumer only needs to handle camelCase.
      const adminHistoryRaw = refreshResponse.data.adminHistory ?? (refreshResponse.data as any).adminhistory;
      const adminHistoryData: typeof adminHistoryRaw = adminHistoryRaw
        ? { adminHistory: adminHistoryRaw.adminHistory ?? (adminHistoryRaw as any).adminhistory ?? [] }
        : undefined;
      console.log('✅ processServiceRequestWithRefresh: Backend refresh token successful');
      console.log(`   📋 Profiles in refresh response: ${profileData?.profiles?.length || 0}`);
      console.log(`   📋 Admin history in refresh response: ${adminHistoryData?.adminHistory?.length || 0}`);
      
      // Update stored tokens using the storage reference
      if (storage && storage.setItem) {
        await storage.setItem('auth_token', newToken);
        if (newRefreshToken) {
          await storage.setItem('refresh_token', newRefreshToken);
        }
        await storage.setItem('auth_timestamp', new Date().toISOString());
      }
      
      // Update API service with new token
      this.setAuthToken(newToken);
      
      // Update user session with new tokens
      if (userStoreState.session?.user) {
        userStoreState.session.user.authToken = newToken;
        if (newRefreshToken) {
          userStoreState.session.user.refreshToken = newRefreshToken;
        }
        
        // Update stored user session using the storage reference
        if (storage && storage.setItem) {
          const sessionData = {
            user: userStoreState.session.user,
            familyAccount: userStoreState.session.familyAccount,
            isAuthenticated: true,
            sessionStartTime: userStoreState.session.sessionStartTime || new Date().toISOString()
          };
          await storage.setItem('userSession', JSON.stringify(sessionData));
        }
      }

      // Sync all backend data using common function
      if (userStore.syncBackendFromResponse) {
        console.log('🔄 processServiceRequestWithRefresh: Starting backend sync after token refresh...');
        try {
          const familyId = userStoreState.session?.user?.familyId || refreshResponse.data.user?.accountId;
          // Get global profile store for profile sync with multiple fallbacks
          const globalProfileStore = (global as any).__profileStore || (global as any).__childNavStores?.useProfileStore || (window as any)?.__profileStore;
          
          console.log('🔍 ApiService: Profile store availability check:', {
            globalProfileStore: !!((global as any).__profileStore),
            childNavStoresProfileStore: !!((global as any).__childNavStores?.useProfileStore),
            windowProfileStore: !!(typeof window !== 'undefined' && (window as any)?.__profileStore),
            selectedStore: !!globalProfileStore
          });
          
          const syncResult = await userStore.syncBackendFromResponse({
            profileData: profileData,
            adminHistoryData: adminHistoryData,
            familyId: familyId,
            profileStore: globalProfileStore,
            contextDescription: 'token_refresh'
          });
          
          console.log(`🔄 processServiceRequestWithRefresh: Backend sync result: ${syncResult.message}`);
          if (!syncResult.success) {
            console.warn('⚠️ processServiceRequestWithRefresh: Some backend sync operations encountered issues');
            if (syncResult.errors && syncResult.errors.length > 0) {
              console.warn('   Token refresh sync errors:', syncResult.errors);
            }
          }
        } catch (syncError) {
          console.error('🔄 processServiceRequestWithRefresh: Backend sync error:', syncError);
          // Don't fail the token refresh due to sync issues
        }
      } else {
        console.warn('⚠️ processServiceRequestWithRefresh: syncBackendFromResponse not available');
      }
      
      return true;
    } else {
      const refreshError = refreshResponse?.message || 'Unknown refresh error';
      console.warn('🔐 processServiceRequestWithRefresh: Backend refresh token failed:', refreshError);
      
      // Check if this is an invalid/expired refresh token error
      if (refreshError.includes('Invalid or expired refresh token') || 
          refreshError.includes('invalid') || 
          refreshError.includes('expired')) {
        console.log('🔐 processServiceRequestWithRefresh: Refresh token is expired/invalid - clearing ONLY refresh token');
        
        // Only clear the refresh_token — do NOT clear auth_token here.
        // Removing auth_token would break any concurrent or subsequent request that still
        // carries a valid access token (e.g., immediately after a fresh passkey sign-in
        // where processServiceRequestWithRefresh is called before refreshAuth restores
        // the in-memory session). The access token is cleared only when the original
        // request itself returns 401, not when the refresh side-channel fails.
        if (storage && storage.removeItem) {
          try {
            await storage.removeItem('refresh_token');
            console.log('🗑️ processServiceRequestWithRefresh: Cleared invalid refresh token from storage');
          } catch (clearError) {
            console.warn('🗑️ processServiceRequestWithRefresh: Failed to clear refresh token:', clearError);
          }
        }
      }
      
      return false;
    }
  }

  /**
   * Process service request with automatic 401 retry handling
   * This method wraps processServiceRequest with automatic JWT token refresh on 401 errors
   */
  async processServiceRequestWithRefresh(
    request: ServiceRequestDto,
    serviceType: ServiceType = 'sync',
    options?: {
      userStore?: any; // UserStore instance for authentication and token management 
      storage?: any; // Storage interface for token persistence
      checkNetworkConnectivity?: () => Promise<boolean>; // Function to check network connectivity
    }
  ): Promise<ServiceResponseDto> {
    try {
      const result = await this.processServiceRequest(request, serviceType);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ processServiceRequestWithRefresh: processServiceRequest threw', {
        message: errorMessage,
        status: (error as any)?.response?.status,
        error,
      });
      
      // Check if this is a 401 authentication error
      const is401Error = (error as any)?.response?.status === 401 ||
                        (error as any)?.status === 401 ||
                        errorMessage.includes('401') ||
                        errorMessage.includes('Authentication required') ||
                        errorMessage.includes('Invalid or missing JWT token');

      if (!is401Error) {
        // Not a 401 error, re-throw original error
        console.log('❌ processServiceRequestWithRefresh: Non-401 error, re-throwing:', errorMessage);
        throw error;
      }

      console.log('🔐 processServiceRequestWithRefresh: 401 detected, attempting automatic re-authentication...');

      // Validate required options for 401 handling
      if (!options?.userStore) {
        console.error('❌ processServiceRequestWithRefresh: userStore required for 401 handling but not provided');
        throw new Error('Authentication error: userStore required for token refresh');
      }

      try {
        // Check network connectivity first if function provided
        if (options.checkNetworkConnectivity) {
          const hasNetwork = await options.checkNetworkConnectivity();
          if (!hasNetwork) {
            console.log('❌ processServiceRequestWithRefresh: No network connectivity - will not retry');
            throw new Error('Session expired - no network connection');
          }
        }

        // Step 1: Try to refresh authentication using the actual refresh token API
        const userStore = options.userStore;
        console.log('🔐 processServiceRequestWithRefresh: Attempting API refresh token...');
        
        let refreshSuccess = false;
        let storedRefreshToken: string | null = null;
        let refreshResponse: any = null;
        
        try {
          // Debug: Check storage availability
          const storage = options.storage || (global as any).__childNavStorage;
          console.log(`📁 processServiceRequestWithRefresh: Storage available: ${!!storage}`);
          
          // Get current userStore state
          const userStoreState = userStore.getState ? userStore.getState() : userStore;
          
          // Debug user session state  
          console.log(`👤 processServiceRequestWithRefresh: UserStore session state:`, {
            hasUser: !!userStoreState.session?.user,
            isAuthenticated: userStoreState.session?.isAuthenticated,
            userEmail: userStoreState.session?.user?.email,
            hasAuthToken: !!userStoreState.session?.user?.authToken,
            hasRefreshToken: !!userStoreState.session?.user?.refreshToken,
            sessionValid: !!userStoreState.isSessionValid && userStoreState.isSessionValid()
          });
          
          // First check if refresh token is available in UserStore session (most recent)
          const sessionRefreshToken = userStoreState.session?.user?.refreshToken;
          
          // Fallback to storage if not in session
          const storageRefreshToken = storage ? await storage.getItem?.('refresh_token') : null;
          
          // Use the most recent refresh token (session takes precedence)
          storedRefreshToken = sessionRefreshToken || storageRefreshToken;
          
          console.log(`🔑 processServiceRequestWithRefresh: Session refresh token found: ${!!sessionRefreshToken}`);
          console.log(`🔑 processServiceRequestWithRefresh: Storage refresh token found: ${!!storageRefreshToken}`);
          console.log(`🔑 processServiceRequestWithRefresh: Using refresh token: ${!!storedRefreshToken}`);
          
          if (storedRefreshToken && this) {
            console.log('🔄 processServiceRequestWithRefresh: Calling backend refresh token API...');
            
            // Get user info for SraId and AccessType from userStore
            const currentUser = userStoreState.session?.user;
            const sraId = currentUser?.userId || (currentUser as any)?.id || '';
            const accessType = '5'; // Global default access type for this app
            
            console.log('🔍 processServiceRequestWithRefresh: RefreshTokenRequestDto fields:', {
              refreshToken: '[PRESENT]',
              SraId: sraId,
              AccessType: accessType
            });
            
            // Call the actual refresh token endpoint
            refreshResponse = await this.refreshToken({
              refreshToken: storedRefreshToken,
              SraId: sraId,
              AccessType: accessType
            });
            
            // Process the refresh response using common logic
            refreshSuccess = await this.processRefreshResponse(
              refreshResponse,
              userStore,
              userStoreState,
              storage
            );
          } else {
            const errorMsg = !storedRefreshToken 
              ? 'No refresh token available for backend refresh' 
              : 'No API service available for backend refresh';
            console.warn(`🔐 processServiceRequestWithRefresh: ${errorMsg}`);
          }
        } catch (refreshError) {
          console.warn('🔐 processServiceRequestWithRefresh: Backend refresh token error:', refreshError);
        }
        
        // Step 2: If backend refresh failed, try fallback to stored credentials
        if (!refreshSuccess && userStore.refreshAuth) {
          console.log('🔐 processServiceRequestWithRefresh: Backend refresh failed, trying local storage fallback...');
          try {
            refreshSuccess = await userStore.refreshAuth();
            if (refreshSuccess) {
              console.log('🔐 processServiceRequestWithRefresh: Local storage fallback successful');
            }
          } catch (storedAuthError) {
            console.warn('🔐 processServiceRequestWithRefresh: Local storage fallback failed:', storedAuthError);
          }
        }
        
        // Step 3: If we successfully restored authentication, retry the request
        if (refreshSuccess) {
          console.log('✅ processServiceRequestWithRefresh: Authentication restored, retrying request...');
          
          try {
            console.log('🔄 processServiceRequestWithRefresh: Making retry request...');
            const retryResponse = await this.processServiceRequest(request, serviceType);
            
            console.log('📬 processServiceRequestWithRefresh: Retry response received:', {
              success: retryResponse?.success,
              hasPayload: !!retryResponse?.payload,
              timestamp: new Date().toISOString()
            });
            
            return retryResponse;
          } catch (retryError) {
            console.error('❌ processServiceRequestWithRefresh: Retry request failed:');
            console.error('🔍 Error type:', typeof retryError);
            console.error('🔍 Error message:', (retryError as any)?.message);
            
            const retryErrorMessage = retryError instanceof Error ? retryError.message : String(retryError);
            console.error(`❌ processServiceRequestWithRefresh: Final retry error: ${retryErrorMessage}`);
            
            throw new Error(`Auth retry error: ${retryErrorMessage}`);
          }
        } else {
          // Authentication restoration failed - determine the reason
          console.log('❌ processServiceRequestWithRefresh: Could not restore authentication');
          
          // Check if we had an expired refresh token
          const hadExpiredRefreshToken = storedRefreshToken && 
            (refreshResponse?.message?.includes('Invalid or expired refresh token') || 
             refreshResponse?.message?.includes('invalid') || 
             refreshResponse?.message?.includes('expired'));
          
          if (hadExpiredRefreshToken) {
            console.log('🔐 processServiceRequestWithRefresh: All tokens expired - manual login required');
            throw new Error('Session expired - manual login required');
          } else if (!storedRefreshToken) {
            console.log('🔐 processServiceRequestWithRefresh: No refresh token available - manual login required');
            throw new Error('No refresh token - manual login required');
          } else {
            console.log('🔐 processServiceRequestWithRefresh: Authentication recovery failed - manual login required');
            throw new Error('Auth recovery failed - manual login required');
          }
        }
        
      } catch (connectivityError) {
        console.error('❌ processServiceRequestWithRefresh: Re-authentication process failed:', connectivityError);
        throw new Error(`Session expired - re-auth process failed: ${connectivityError instanceof Error ? connectivityError.message : String(connectivityError)}`);
      }
    }
  }

  /**
   * Sync service request
   */
  async syncService(request: ServiceRequestDto): Promise<ServiceResponseDto> {
    return this.processServiceRequest(request, 'sync');
  }

  /**
   * Content service request
   */
  async contentService(request: ServiceRequestDto): Promise<ServiceResponseDto> {
    return this.processServiceRequest(request, 'content');
  }

  /**
   * Account service request
   */
  async accountService(request: ServiceRequestDto): Promise<ServiceResponseDto> {
    return this.processServiceRequest(request, 'account');
  }

  /**
   * Data service request
   */
  async dataService(request: ServiceRequestDto): Promise<ServiceResponseDto> {
    return this.processServiceRequest(request, 'data');
  }

  /**
   * Notification service request
   */
  async notifyService(request: ServiceRequestDto): Promise<ServiceResponseDto> {
    return this.processServiceRequest(request, 'notify');
  }

  /**
   * Logging service request
   */
  async logService(request: ServiceRequestDto): Promise<ServiceResponseDto> {
    return this.processServiceRequest(request, 'log');
  }

  /**
   * Marketing service request
   */
  async mktService(request: ServiceRequestDto): Promise<ServiceResponseDto> {
    return this.processServiceRequest(request, 'mkt');
  }

  /**
   * Reporting service request
   */
  async reportService(request: ServiceRequestDto): Promise<ServiceResponseDto> {
    return this.processServiceRequest(request, 'report');
  }

  /**
   * Request a verification code to be sent to the given email address.
   * The backend is expected to deliver a short-lived OTP via email/SMS.
   */
  async sendVerificationCode(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.httpAdapter.post<{ success: boolean; message: string }>(
        this.authEndpoints.SEND_CODE,
        { email },
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data ?? { success: true, message: 'Code sent' };
    } catch (error: any) {
      const status = error?.response?.status ?? 0;
      const message = error?.response?.data?.message ?? error?.message ?? 'Failed to send code';
      // Treat 404 as endpoint-not-implemented (backend not ready yet)
      if (status === 404) {
        return { success: false, message: 'Verification code endpoint not available on this server' };
      }
      return { success: false, message };
    }
  }

  /**
   * Request a WebAuthn / passkey challenge from the backend.
   *
   * POST /api/Authentication/passkey/challenge
   * Body: { email, accessType, userId? }
   *
   * The backend returns:
   *   { success, data: { challengeId, challenge, relyingPartyId, relyingPartyName,
   *                      userVerification, timeout,
   *                      allowCredentials? (auth) | user? (registration) } }
   *
   * Callers inspect `data.allowCredentials` to decide authenticate vs. register.
   */
  async getPasskeyChallenge(
    email: string,
    accessType: string,
    userId?: string | null,
    userName?: string | null,
  ): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      const body: Record<string, unknown> = { email, accessType };
      if (userId) body.userId = userId;
      if (userName)   body.userName  = userName;
      const response = await this.httpAdapter.post<any>(
        this.authEndpoints.PASSKEY_CHALLENGE,
        body,
        { headers: { 'Content-Type': 'application/json' } }
      );
      const envelope = response.data;
      // Backend wraps response in { success, data, message }
      if (envelope?.success === false) {
        return { success: false, message: envelope.message ?? 'Challenge request failed' };
      }
      return { success: true, data: envelope?.data ?? envelope };
    } catch (error: any) {
      const message = error?.response?.data?.message ?? error?.message ?? 'Failed to get passkey challenge';
      return { success: false, message };
    }
  }

  /**
   * Submit a WebAuthn credential response to the backend for verification / registration.
   *
   * POST /api/Authentication/passkey/verify
   * Body: { challengeId, email, accessType, assertion }
   *
   * Returns a session on success: { token, refreshToken, user, ... }
   */
  async verifyPasskeyAssertion(
    challengeId: string,
    email: string,
    accessType: string,
    assertion: Record<string, unknown>,
  ): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      // Body keys must match C# Pascal-case DTO properties exactly:
      // PasskeyVerifyRequestDto: ChallengeId, Email, AccessType, Assertion
      const body = {
        ChallengeId: challengeId,
        Email:       email,
        AccessType:  accessType,
        Assertion:   assertion,
      };

      const response = await this.httpAdapter.post<any>(
        this.authEndpoints.PASSKEY_VERIFY,
        body,
        { headers: { 'Content-Type': 'application/json' } }
      );
      const envelope = response.data;
      if (envelope?.success === false) {
        return { success: false, message: envelope.message ?? 'Passkey verification failed' };
      }
      return { success: true, data: envelope?.data ?? envelope };
    } catch (error: any) {
      const message = error?.response?.data?.message ?? error?.message ?? 'Passkey verification failed';
      return { success: false, message };
    }
  }

  /**
   * Set authentication token for subsequent requests
   */
  setAuthToken(token: string | null): void {
    console.log('🔐 ApiService: Setting auth token:', token ? '[SET]' : '[CLEARED]');
    this.httpAdapter.setAuthToken(token);
  }

  /**
   * Get current authentication token
   */
  getAuthToken(): string | null {
    return this.httpAdapter.getAuthToken();
  }

  /**
   * Initialize API service with stored credentials
   */
  async initializeWithStorage(storage: any): Promise<void> {
    console.log('🔄 ApiService: Initializing with stored credentials...');
    
    try {
      const storedToken = await storage.getItem('auth_token');
      if (storedToken) {
        console.log('✅ ApiService: Found stored auth token, setting for API requests');
        this.setAuthToken(storedToken);
        
        // Optionally verify token is still valid
        const userEmail = await storage.getItem('user_email');
        console.log('📧 ApiService: Associated email:', userEmail || 'Not found');
      } else {
        console.log('ℹ️ ApiService: No stored auth token found, API will use anonymous requests');
      }
    } catch (error) {
      console.error('❌ ApiService: Error loading stored credentials:', error);
    }
  }
  /**
   * Delete a child profile from the backend.
   * TODO: Implement once the backend DELETE /profiles/{profileId} endpoint is available.
   * @param profileId - The ID of the profile to delete
   * @param accountId - The parent account ID that owns the profile
   */
  async deleteProfile(profileId: string, accountId: string): Promise<void> {
    // Placeholder — backend endpoint not yet implemented.
    // When ready, replace with:
    //   await this.httpAdapter.delete(`${this.baseUrl}/profiles/${profileId}`, {
    //     headers: { Authorization: `Bearer ${this.authToken}` },
    //   });
    console.warn(
      `⚠️ ApiService.deleteProfile: Backend endpoint not yet implemented. ` +
      `Profile ${profileId} (account ${accountId}) removed locally only.`
    );
  }}